import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#applyStoredLayout();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Applies the stored grid layout for the current viewport.
   */
  #applyStoredLayout() {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const storedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';
    this.#setLayout(storedLayout);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Updates the grid attribute and syncs matching radio controls.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);

    const inputName = mediaQueryLarge.matches ? 'grid' : 'grid-mobile';
    this.querySelectorAll(`input[type="radio"][name="${inputName}"]`).forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input.value === value;
      }
    });
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    const storedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';
    this.#setLayout(storedLayout);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
